import * as React from 'react';
import { Search, VideoOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '@cguard/design-system';
import type { BrandedCameraSummary as CameraSummary } from '@cguard/api-client/hooks/cameras';
import type { CameraId } from '@cguard/types';
import { useCameraStatusLabel } from '~/lib/camera-status';
import { useSiteNames } from '~/lib/site-names';

export function CameraRail({
  cameras,
  selectedId,
  onSelect,
  annotate,
  siteFirst = false,
}: {
  siteFirst?: boolean;
  cameras: CameraSummary[];
  selectedId: CameraId | null;
  onSelect: (cameraId: CameraId) => void;
  /** Optional short note shown under a camera, such as missing recordings on Playback. */
  annotate?: (camera: CameraSummary) => string | null;
}): React.JSX.Element {
  const { t } = useTranslation('video');
  const statusLabel = useCameraStatusLabel();
  const [query, setQuery] = React.useState('');
  const siteNameById = useSiteNames();
  const [site, setSite] = React.useState('');
  const activeSite = site || cameras.find((camera) => camera.id === selectedId)?.siteId || cameras[0]?.siteId || '';
  const needle = query.trim().toLowerCase();
  const visible = cameras.filter(
    (camera) =>
      (!siteFirst || camera.siteId === activeSite) && (camera.name.toLowerCase().includes(needle) ||
      (siteNameById.get(camera.siteId)?.toLowerCase().includes(needle) ?? false)),
  );

  return (
    <aside className="camera-rail" aria-label={t('rail.label')}>
      <div className="rail-heading">
        <div>
          <p className="eyebrow">{t('rail.eyebrow')}</p>
          <h2>{t('rail.count', { count: cameras.length })}</h2>
        </div>
        <span className="live-count">
          {t('rail.liveCount', {
            count: cameras.filter((camera) => camera.status === 'live').length,
          })}
          <span className="mobile-rail-hint" aria-hidden>
            {' '}
            {t('rail.swipeHint')}
          </span>
        </span>
      </div>
      {siteFirst && <label className="search-field">{t('exports.site')}<select aria-label={t('exports.site')} value={activeSite} onChange={(event) => {
        setSite(event.target.value); const first = cameras.find((camera) => camera.siteId === event.target.value); if (first) onSelect(first.id);
      }}>{[...new Set(cameras.map((camera) => camera.siteId))].map((id) => <option key={id} value={id}>{siteNameById.get(id) ?? id}</option>)}</select></label>}
      <label className="search-field">
        <Search aria-hidden size={14} />
        <span className="sr-only">{t('rail.search')}</span>
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('rail.searchPlaceholder')}
          type="search"
          className="focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </label>
      <div className="camera-list">
        {visible.length === 0 ? (
          <div className="empty-rail">
            <VideoOff aria-hidden size={18} />
            <span>{t('rail.noMatch')}</span>
            {needle !== '' ? (
              <button type="button" className="text-button" onClick={() => setQuery('')}>
                {t('rail.clearSearch')}
              </button>
            ) : null}
          </div>
        ) : (
          visible.map((camera, index) => {
            const siteName = siteNameById.get(camera.siteId);
            const note = annotate?.(camera) ?? null;
            return (
              <button
                key={camera.id}
                type="button"
                className={`camera-row ${camera.id === selectedId ? 'is-selected' : ''}`}
                aria-pressed={camera.id === selectedId}
                onClick={() => onSelect(camera.id)}
              >
                <span className="camera-index">{String(index + 1).padStart(2, '0')}</span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="camera-name">{camera.name}</span>
                  {siteName ? <span className="camera-location">{siteName}</span> : null}
                  {note ? <span className="camera-note">{note}</span> : null}
                </span>
                <span className={`camera-status status-${camera.status}`}>
                  <span className="status-dot" aria-hidden />
                  <span>{statusLabel(camera.status)}</span>
                </span>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
}
